import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState(new Map()); // threadId -> Set of userIds
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Initialize socket connection
  useEffect(() => {
    // Only connect if we have both user and token
    // This prevents connection attempts when user is null (network error case)
    if (!user || !token) {
      // Disconnect if user logs out or user data not available
      if (socketRef.current) {
        console.log("🔌 Disconnecting WebSocket (no user or token)");
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    console.log("🔌 Initializing WebSocket connection for user:", user.email);

    // Derive the socket server URL from VITE_API_URL.
    // VITE_API_URL may include a sub-path (e.g. "/puffin/api/v1") because the
    // app is served behind a reverse proxy. Strip everything after the host so
    // Socket.io connects to the server root. The socket PATH is derived from
    // the same sub-path prefix (e.g. "/puffin" -> path: "/puffin/socket.io").
    const rawApiUrl =
      import.meta.env.VITE_API_URL || "http://localhost:5001/api/v1";

    let socketServerUrl = "http://localhost:5001";
    let socketPath = "/socket.io"; // Socket.io default

    try {
      const parsed = new URL(rawApiUrl);
      socketServerUrl = `${parsed.protocol}//${parsed.host}`;

      // Derive the socket.io path from the proxy prefix.
      // e.g. pathname="/puffin/api/v1" → prefix="/puffin" → path="/puffin/socket.io"
      // e.g. pathname="/api/v1"        → prefix=""         → path="/socket.io"
      const pathParts = parsed.pathname.split("/").filter(Boolean);
      // The first segment is the proxy prefix IF it is not "api"
      if (pathParts.length > 0 && pathParts[0] !== "api") {
        socketPath = `/${pathParts[0]}/socket.io`;
      }
    } catch {
      socketServerUrl = "http://localhost:5001";
    }

    // Create socket connection
    const newSocket = io(socketServerUrl, {
      path: socketPath,
      auth: {
        token: token,
      },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    // Connection event handlers
    newSocket.on("connect", () => {
      console.log("✅ WebSocket connected");
      setIsConnected(true);

      // Clear any pending reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    });

    newSocket.on("disconnect", (reason) => {
      console.log("🔌 WebSocket disconnected:", reason);
      setIsConnected(false);

      // Attempt to reconnect after a delay
      if (reason === "io server disconnect") {
        // Server disconnected, try to reconnect
        reconnectTimeoutRef.current = setTimeout(() => {
          newSocket.connect();
        }, 2000);
      }
    });

    newSocket.on("connect_error", (error) => {
      console.error("❌ WebSocket connection error:", error.message);
      setIsConnected(false);
    });

    // Presence updates
    newSocket.on("presence:update", (data) => {
      const { userId, status } = data;
      setOnlineUsers((prev) => {
        const updated = new Set(prev);
        if (status === "online") {
          updated.add(userId);
        } else {
          updated.delete(userId);
        }
        return updated;
      });
    });

    // Typing indicators
    newSocket.on("typing:started", (data) => {
      const { threadId, userId } = data;
      setTypingUsers((prev) => {
        const updated = new Map(prev);
        if (!updated.has(threadId)) {
          updated.set(threadId, new Set());
        }
        updated.get(threadId).add(userId);
        return updated;
      });
    });

    newSocket.on("typing:stopped", (data) => {
      const { threadId, userId } = data;
      setTypingUsers((prev) => {
        const updated = new Map(prev);
        if (updated.has(threadId)) {
          updated.get(threadId).delete(userId);
          if (updated.get(threadId).size === 0) {
            updated.delete(threadId);
          }
        }
        return updated;
      });
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      newSocket.disconnect();
    };
  }, [user, token]);

  // Send message
  const sendMessage = useCallback(
    (threadId, messageData) => {
      if (socket && isConnected) {
        socket.emit("message:send", {
          threadId,
          ...messageData,
        });
      }
    },
    [socket, isConnected],
  );

  // Mark message as delivered
  const markMessageDelivered = useCallback(
    (messageId, employeeId) => {
      if (socket && isConnected) {
        socket.emit("message:delivered", {
          messageId,
          employeeId,
        });
      }
    },
    [socket, isConnected],
  );

  // Mark message as read
  const markMessageRead = useCallback(
    (messageId, threadId, employeeId) => {
      if (socket && isConnected) {
        socket.emit("message:read", {
          messageId,
          threadId,
          employeeId,
        });
      }
    },
    [socket, isConnected],
  );

  // Start typing indicator
  const startTyping = useCallback(
    (threadId) => {
      if (socket && isConnected) {
        socket.emit("typing:start", { threadId });
      }
    },
    [socket, isConnected],
  );

  // Stop typing indicator
  const stopTyping = useCallback(
    (threadId) => {
      if (socket && isConnected) {
        socket.emit("typing:stop", { threadId });
      }
    },
    [socket, isConnected],
  );

  // Update presence status
  const updatePresence = useCallback(
    (status) => {
      if (socket && isConnected) {
        socket.emit("presence:update", { status });
      }
    },
    [socket, isConnected],
  );

  // Join a thread room
  const joinThread = useCallback(
    (threadId) => {
      if (socket && isConnected) {
        socket.emit("thread:join", { threadId });
      }
    },
    [socket, isConnected],
  );

  // Leave a thread room
  const leaveThread = useCallback(
    (threadId) => {
      if (socket && isConnected) {
        socket.emit("thread:leave", { threadId });
      }
    },
    [socket, isConnected],
  );

  // Subscribe to events
  const on = useCallback(
    (event, handler) => {
      if (socket) {
        socket.on(event, handler);
        return () => socket.off(event, handler);
      }
      return () => {};
    },
    [socket],
  );

  // Unsubscribe from events
  const off = useCallback(
    (event, handler) => {
      if (socket) {
        socket.off(event, handler);
      }
    },
    [socket],
  );

  // Tell the backend which direct-message conversation the user is currently
  // viewing, so the backend can suppress duplicate push notifications for
  // messages the user is literally watching arrive.
  const setViewingConversation = useCallback(
    (employeeId) => {
      if (socket && isConnected) {
        socket.emit("direct_message:viewing", { employeeId });
      }
    },
    [socket, isConnected],
  );

  const clearViewingConversation = useCallback(() => {
    if (socket && isConnected) {
      socket.emit("direct_message:viewing_ended");
    }
  }, [socket, isConnected]);

  const value = {
    socket,
    isConnected,
    onlineUsers,
    typingUsers,
    sendMessage,
    markMessageDelivered,
    markMessageRead,
    startTyping,
    stopTyping,
    updatePresence,
    joinThread,
    leaveThread,
    on,
    off,
    setViewingConversation,
    clearViewingConversation,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
